import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import {
  IMPACT_REPORT_DOWNLOAD_BUTTON_LABEL,
  IMPACT_REPORT_FALLBACK_IMAGE_SRC,
  IMPACT_REPORT_GFG_MARK_SRC,
  IMPACT_REPORT_PDF_DOWNLOAD_PENDING_LABEL,
  IMPACT_REPORT_PDF_EXPORT_ENABLED,
} from "@/constants/impactReport";
import { useImpactReportPdfDownload } from "@/hooks/useImpactReportPdfDownload";
import type { ImpactReportDocumentRenderOptions } from "@/interfaces/impactReportDocument";
import type {
  ImpactReportCausePage,
  ImpactReportClosingPage,
  ImpactReportCoverPage,
  ImpactReportPage,
  ImpactReportStatistic,
} from "@/interfaces/impactReportView";

interface ImpactHeroPageProps extends ImpactReportDocumentRenderOptions {
  page: ImpactReportPage;
}

/**
 * One full-bleed page of the impact report, rendered from the page model — the components read
 * a view-model and never the payload, so nothing here decides what the document says.
 *
 * Every page draws a backdrop, so every page gets `fallbackBackdropSrc`; the GfG mark and the
 * download control are drawn by the CLOSING page only, so only it gets the other two options.
 */
export function ImpactHeroPage({
  page,
  gfgMarkSrc,
  fallbackBackdropSrc,
  omitDownloadAffordance,
}: ImpactHeroPageProps) {
  switch (page.kind) {
    case "cover":
      return <ImpactHeroCoverPage page={page} fallbackBackdropSrc={fallbackBackdropSrc} />;
    case "cause":
      return <ImpactHeroCausePage page={page} fallbackBackdropSrc={fallbackBackdropSrc} />;
    case "closing":
      return (
        <ImpactHeroClosingPage
          page={page}
          gfgMarkSrc={gfgMarkSrc}
          fallbackBackdropSrc={fallbackBackdropSrc}
          omitDownloadAffordance={omitDownloadAffordance}
        />
      );
    default: {
      const unhandledPage: never = page;
      console.error("ImpactHeroPage: unhandled page kind in the page model", unhandledPage);
      return null;
    }
  }
}

/**
 * The page shell's classes for a given backdrop.
 *
 * A page whose `backdropUrl` is `null` paints no `<img>` at all (see `ImpactHeroBackdrop`), and
 * `.irv-page` itself declares no background-color — it sits directly on `.irv-doc`, which is
 * WHITE. Without an opaque ground of its own, that page's white copy would land on nothing but
 * the translucent scrim over white: ~3:1 at the scrim's darkest point and effectively white-on-
 * white where it thins to 0.06 alpha (the cover's third summary figure, the closing page's
 * Download label and the white GfG mark), in a document a client forwards onward.
 *
 * So the no-backdrop page gets a dark ground instead — NOT a stand-in photograph. The bundled
 * fallback stays reserved for a URL that fails to load; an ABSENT image still means "no photo".
 * Image-backed pages are visually unchanged, since `.irv-page-bg` covers `inset: 0`.
 *
 * The ground is `.irv-page--bare` in `styles/impact-report-document.css`, NOT a Tailwind utility:
 * the serialised PDF document embeds that stylesheet but runs no Tailwind build, so a utility
 * class here would print this page white-on-white.
 */
function buildPageClassName(backdropUrl: string | null, ...modifiers: string[]): string {
  const classNames = ["irv-page", ...modifiers];
  if (backdropUrl === null) classNames.push("irv-page--bare");
  return classNames.join(" ");
}

type ImpactHeroBackdropOptions = Pick<ImpactReportDocumentRenderOptions, "fallbackBackdropSrc">;

interface ImpactHeroCoverPageProps extends ImpactHeroBackdropOptions {
  page: ImpactReportCoverPage;
}

/**
 * P1: the kicker, the wire's tagline, and the three summary statistics in one row.
 *
 * An empty `kicker` is the builder's EXPLICITLY-ABSENT signal (no org name on the wire), so the
 * element is omitted rather than emitted empty — same convention as a `null` statistic value.
 */
function ImpactHeroCoverPage({ page, fallbackBackdropSrc }: ImpactHeroCoverPageProps) {
  return (
    <section className={buildPageClassName(page.backdropUrl)}>
      <ImpactHeroBackdrop src={page.backdropUrl} fallbackSrc={fallbackBackdropSrc} />
      <div className="irv-inner">
        {page.kicker !== "" && <div className="irv-kicker">{page.kicker}</div>}
        <div className="irv-title">{page.title}</div>
        {/* All three figures render straight off the payload, no recomputation — but two of them
            (impact vendors, impact types) are DELIBERATELY frozen against a cause filter, and
            that is not a bug to "fix" by recomputing them from the filtered `causeData` below.
            They are counted from the account's headline totals, a different source than the
            per-cause census `causeData` is built from, so narrowing the report to one cause
            changes which cause PAGES print without changing these two numbers at all. (The first
            figure, the causes-touched count, is NOT in that group — it comes from the same
            census as `causeData` and is expected to narrow along with it.) Making the vendor/type
            counts match the filtered view would have the cover contradict the account's real
            totals just to look consistent with a page selection underneath it. */}
        <div className="irv-sum-stats">
          {page.statistics.map((statistic) => (
            <ImpactHeroStatistic
              key={statistic.statisticKey}
              statistic={statistic}
              valueClassName="irv-sum-value"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface ImpactHeroCausePageProps extends ImpactHeroBackdropOptions {
  page: ImpactReportCausePage;
}

/**
 * P2–Pn: one page per `data.causeData` entry, emitted by the builder in the server's own array
 * order.
 *
 * `title` is `CauseSection.causeName` — the authored "-ing" label the client's report copy uses.
 * The opaque `causeValue` ("Care for Children") reaches no element here: rendering it would
 * print "…IS CARE FOR CHILDREN" where the report says "…IS CARING FOR CHILDREN".
 *
 * The grid element is omitted rather than emitted empty when a cause resolves to no statistics
 * at all — its own top margin would otherwise hang below the headline as unexplained space. An
 * empty `kicker` is omitted for the same reason: it means the payload carried no org name.
 */
function ImpactHeroCausePage({ page, fallbackBackdropSrc }: ImpactHeroCausePageProps) {
  return (
    <section className={buildPageClassName(page.backdropUrl)}>
      <ImpactHeroBackdrop src={page.backdropUrl} fallbackSrc={fallbackBackdropSrc} />
      <div className="irv-inner">
        {page.kicker !== "" && <div className="irv-kicker">{page.kicker}</div>}
        <div className="irv-title">{page.title}</div>
        {page.statistics.length > 0 && (
          <div className="irv-stats">
            {page.statistics.map((statistic) => (
              <ImpactHeroStatistic
                key={statistic.statisticKey}
                statistic={statistic}
                valueClassName="irv-stat-value"
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

interface ImpactHeroClosingPageProps extends ImpactReportDocumentRenderOptions {
  page: ImpactReportClosingPage;
}

/**
 * P6: the centered thank-you line and the white GfG mark at the foot of the page. `.irv-closing`
 * swaps the other pages' directional scrim for a soft centered vignette, since this page's copy
 * is centered rather than left-aligned.
 *
 * The Download PDF affordance sits behind `IMPACT_REPORT_PDF_EXPORT_ENABLED`, which is on: it is
 * `ImpactHeroClosingDownloadButton` below, wired to the same `useImpactReportPdfDownload` — and so
 * the same download — as the control bar's download control.
 *
 * `omitDownloadAffordance` overrides the flag for the SERIALISED document: a PDF that offers to
 * download itself is wrong, so the control is left out of that document's DOM rather than hidden
 * in it with print CSS. It is also what keeps the serialiser off the hook: that render mounts no
 * `ImpactReportProvider` and no query client, and the hook reads both, so the hook lives in a child
 * that is never instantiated there rather than in this component.
 */
function ImpactHeroClosingPage({
  page,
  gfgMarkSrc = IMPACT_REPORT_GFG_MARK_SRC,
  fallbackBackdropSrc,
  omitDownloadAffordance = false,
}: ImpactHeroClosingPageProps) {
  return (
    <section className={buildPageClassName(page.backdropUrl, "irv-closing")}>
      <ImpactHeroBackdrop src={page.backdropUrl} fallbackSrc={fallbackBackdropSrc} />
      <div className="irv-inner">
        <div className="irv-title">{page.title}</div>
        {IMPACT_REPORT_PDF_EXPORT_ENABLED && !omitDownloadAffordance && (
          <ImpactHeroClosingDownloadButton />
        )}
      </div>
      <img className="irv-closing-logo" src={gfgMarkSrc} alt="Gifts for Good" />
    </section>
  );
}

/**
 * The closing page's Download Report button. Shows the shared pending state on its own label; the
 * pending announcement and any failure come from the control bar's `ImpactReportDownloadControl`,
 * which is mounted on `/impact` whenever this is, so neither is said twice.
 */
function ImpactHeroClosingDownloadButton() {
  const { pending, canDownload, download } = useImpactReportPdfDownload();

  return (
    <button
      type="button"
      className="irv-outline-btn"
      disabled={!canDownload || pending}
      aria-busy={pending}
      onClick={download}
    >
      {pending ? (
        <CircularProgress size={18} thickness={5} color="inherit" />
      ) : (
        <FileDownloadOutlined fontSize="small" />
      )}
      {pending ? IMPACT_REPORT_PDF_DOWNLOAD_PENDING_LABEL : IMPACT_REPORT_DOWNLOAD_BUTTON_LABEL}
    </button>
  );
}

interface ImpactHeroStatisticProps {
  statistic: ImpactReportStatistic;
  /** The large-type scale for this grid — the cover sets its three summary figures larger. */
  valueClassName: string;
}

/**
 * One statistic tile: the composed quantity set large, the sentence beneath it as its OWN
 * element. The wire splits quantity, unit and sentence into three fields precisely because a
 * single run-on string cannot produce two type treatments, so they are never concatenated back
 * into one line here.
 *
 * A `null` value is the explicitly-absent slot: NO large-type element is emitted at all, not an
 * empty one, so the descriptor moves to the top of the tile and the grid row leaves none of the
 * height a number would have occupied. Branching on `!== null` rather than on string truthiness
 * keeps that distinct from a quantity that legitimately renders as "0".
 *
 * A statement the server WITHHELD never arrives here — the builder drops those with the whole
 * impact type, so this collapse covers only an authored sentence whose quantity did not resolve.
 */
function ImpactHeroStatistic({ statistic, valueClassName }: ImpactHeroStatisticProps) {
  return (
    <div>
      {statistic.value !== null && <div className={valueClassName}>{statistic.value}</div>}
      {statistic.descriptor !== "" && <div className="irv-stat-desc">{statistic.descriptor}</div>}
    </div>
  );
}

interface ImpactHeroBackdropProps {
  src: string | null;
  /** Where the bundled fallback is read from — see `ImpactReportDocumentRenderOptions`. */
  fallbackSrc?: string;
}

/**
 * The page's backdrop layer. A real `<img>` rather than a CSS `background-image` because a CSS
 * background has no error event, and a photograph URL that 404s has to fall back to the bundled
 * asset rather than leaving a bare page in a document a client forwards onward.
 *
 * `src === null` is EXPLICITLY NO BACKGROUND — the cover or closing page's own image was absent
 * on the payload — and renders no `<img>` at all, not the bundled fallback. That is a different
 * case from a present URL that fails to load, which the `onError` handler below still swaps for
 * the bundled asset; a null `src` never reaches an `<img>` element, so it can never fire `onError`
 * in the first place.
 *
 * A `src` equal to `IMPACT_REPORT_FALLBACK_IMAGE_SRC` is the page model naming the bundled
 * fallback itself, so it is read from `fallbackSrc` like the error swap is.
 */
function ImpactHeroBackdrop({
  src,
  fallbackSrc = IMPACT_REPORT_FALLBACK_IMAGE_SRC,
}: ImpactHeroBackdropProps) {
  if (src === null) return null;

  return (
    <img
      className="irv-page-bg"
      src={src === IMPACT_REPORT_FALLBACK_IMAGE_SRC ? fallbackSrc : src}
      alt=""
      aria-hidden="true"
      onError={(event) => {
        const failedSrc = event.currentTarget.getAttribute("src");
        // Guard against re-entry: swapping the fallback for itself would loop forever.
        if (failedSrc === fallbackSrc) {
          console.error(
            "ImpactHeroPage: the bundled fallback backdrop itself failed to load; the page renders without a backdrop",
            { src: fallbackSrc },
          );
          return;
        }
        console.error("ImpactHeroPage: backdrop image failed to load, using bundled fallback", {
          src: failedSrc,
        });
        event.currentTarget.src = fallbackSrc;
      }}
    />
  );
}
